import React from 'react';
import FlowIconSquare from '@Client/runner.directives/common/FlowIconSquare/FlowIconSquare';
import { getService } from '@Client/runner.hooks/useService';
import FlowModelSummaryTag from '@Client/runner.shared/FlowModelSummaryTag/FlowModelSummaryTag';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import IUserBasicInfo from '@Shared.Angular/@types/core/contracts/queryModel/user/UserBasicInfo';
import { IFlowForDisplay } from '@Client/runner.services/flows.formatter';
import IntegrationState from '@Shared.Angular/@types/core/contracts/queryModel/webhook/integrationState';

type Props = {
  isMobile: boolean;
  flow: IFlowSummary;
  onFlowClick: (flowId: Guid) => void;
};

const FlowSummary = ({ isMobile, flow, onFlowClick }: Props) => {
  const appConfig = getService('APP_CONFIG');
  const flowinglyMomentService = getService('flowinglyMomentService');
  const sessionService = getService('sessionService');
  const showConfidentialFlowOption = appConfig.showConfidentialFlowOption;
  const progress = flow.percentageComplete;
  const webhookIconPath = ASSETS_PATH + '/webhooks-icon.svg';
  const flowHasProcessingWebhook =
    flow.integrationState === IntegrationState.Processing;

  const getStartedByText = (flow: IFlowSummary) => {
    const starter = flow.whoStarted;
    const requester = flow.requestedByUser
      ? getNameToDisplay(flow.requestedByUser)
      : null;

    let text: string;
    if (requester && requester != starter) {
      // if You != You
      const requesterName = getNameToDisplay(flow.requestedByUser);
      text = `Started by ${requesterName} on behalf of ${starter}`;
    } else {
      text = `Started by ${starter}`;
    }
    text += ' ' + flow.momentStarted;

    return text;
  };

  const getNameToDisplay = (user: IUserBasicInfo) => {
    const currentUser = sessionService.getUser();
    if (currentUser.email == user.email) {
      return 'You';
    } else {
      return user.firstName + ' ' + user.lastName;
    }
  };

  let startedByText = '';

  if (flow.flowId == EmptyGuid || flow.flowId == null) {
    startedByText = 'Started by';
  } else {
    startedByText = getStartedByText(flow);
  }

  const renderProgressBar = () => (
    <div
      className="flow-status-progress--container"
      title={`${progress}%`}
      style={{ width: '100%', maxWidth: '100px' }}
    >
      <div
        className="flow-status-progress--bar"
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );

  const renderCommentBubble = () =>
    flow.commentCount > 0 && (
      <span className="flow-list__contents__subject__comment-bubble">
        <i className="fa-light fa-comments fa-lg"></i>
        <span className="flow-list__contents__subject__comment-bubble__text">
          {flow.commentCount}
        </span>
      </span>
    );

  const renderAttachmentIcon = () =>
    flow.filesCount > 0 && (
      <span className="flow-list__contents__subject__attachment">
        <i className="fa-light fa-paperclip fa-lg"></i>
      </span>
    );

  const renderWebhookIcon = () =>
    flowHasProcessingWebhook && (
      <img
        src={webhookIconPath}
        className="flow-list__contents__current-step__webhook"
        alt="Webhook Icon"
        title="Integration in progress"
      />
    );

  const renderDueDate = () => {
    if (flow.isFinalised) {
      return (
        <>
          <div className="flow-list__contents__due-date__text">
            {flow.finalisedReason === 'Withdrawn'
              ? 'Cancelled'
              : flow.finalisedReason}{' '}
            {flow.momentFinalised}
            <div
              className="flow-list__contents__subject__started-by"
              style={{ marginTop: '2px' }}
            >
              {flow.momentFinalisedInLocalFormat}
            </div>
          </div>
        </>
      );
    }
    if (flow.isOverdue) {
      return (
        <div className="flow-list__contents__due-date__text color-warning">
          <i className="fa-light fa-calendar-xmark"></i> {flow.momentDue}
        </div>
      );
    }
    return (
      <div className="flow-list__contents__due-date__text">
        {flow.momentDue !== 'No Due Date' && flow.momentDue !== '' && (
          <i className="fa-light fa-calendar-check"></i>
        )}{' '}
        {flow.momentDue}
      </div>
    );
  };

  const renderDueBy = () =>
    flow.momentDue !== 'No Due Date' && (
      <div className="flow-list__contents__due-date__due-by">
        {flow.isFinalised
          ? flow.finalisedInLocalFormat
          : flow.dueDateInLocalFormat}
      </div>
    );

  const renderWorkflowType = () =>
    showConfidentialFlowOption && (
      <div className="flow-list__contents__workflow-type__block">
        <div className="flow-list__contents__workflow-type__header">
          Flow Type
        </div>
        <div className="flow-list__contents__workflow-type__text">
          <div
            className={`${
              flow.isConfidential
                ? 'flow-list__contents__workflow-type__confidential'
                : 'flow-list__contents__workflow-type__normal'
            } flex-column`}
          >
            {flow.isConfidential ? 'Confidential Workflow' : 'Workflow'}
          </div>
        </div>
      </div>
    );

  if (!isMobile) {
    return (
      <div
        className="flow-header flex flex-row"
        onClick={() => onFlowClick(flow.flowId)}
      >
        <FlowIconSquare name={flow.name} flowCode="" />

        <div className="flow-header__right-container">
          <div className="flow-list__contents flex flex-row">
            <div className="flow-list__contents__flow-status">
              <div className="flow-list__contents__flow-status__identifier">
                {flow.flowIdentifier}
              </div>
              <div className="flow-list__contents__flow-status__progress">
                {renderProgressBar()}
              </div>
            </div>

            <div className="flow-list__contents__subject">
              <div className="flow-list__contents__subject__text">
                <span className="flow-list__contents__subject__text2">
                  {flow.subject}
                </span>
                {renderCommentBubble()}
                {renderAttachmentIcon()}
              </div>
              <div className="flow-list__contents__subject__started-by">
                {startedByText}
              </div>
            </div>

            {!flow.isFinalised && (
              <div className="flow-list__contents__current-step">
                <div className="flow-list__contents__current-step__text">
                  <span>
                    {flow.currentStepsHeader} {renderWebhookIcon()}
                  </span>
                </div>
                <div className="flow-list__contents__current-step__flowed-to">
                  Flowed to {flow.waitingForName}{' '}
                  {flowinglyMomentService.calculateRelativeTimeOrFullDate(
                    flow.waitingForDate,
                    'LL'
                  )}
                </div>
              </div>
            )}

            <div className="flow-list__contents__due-date">
              {renderDueDate()}
              {renderDueBy()}
            </div>

            {renderWorkflowType()}
          </div>
          {appConfig.enableTagging && flow.parsedTags?.length > 0 && (
            <div className="flow-header-right__tag-row">
              <FlowModelSummaryTag
                tags={flow.parsedTags.map((tag) => ({
                  id: tag.id,
                  name: tag.name,
                  color: tag.color
                }))}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="flow-header isMobile"
      onClick={() => onFlowClick(flow.flowId)}
    >
      <div className="flex flow-header__main">
        <FlowIconSquare name={flow.name} flowCode="" />
        <div className="flow-list__contents flex flex-row">
          <div className="flow-list__contents__header--mobile">
            <div className="flow-list__contents__flow-name--mobile">
              <div className="flow-list__contents__flow-name__text flow-list__contents__flow-name__text--mobile">
                {flow.flowIdentifier}
              </div>
            </div>
            <div className="flow-list__contents__flow-status--mobile">
              <div className="flow-list__contents__flow-status__progress">
                {renderProgressBar()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flow-header__extra">
        <div className="flow-list__contents__subject__text flex-row">
          {flow.subject} {renderCommentBubble()}
          {renderAttachmentIcon()}
        </div>
        <div className="flow-list__contents__subject__started-by flow-list__contents__subject__started-by--mobile flex-row">
          {startedByText}
        </div>
        <div className="flow-list__contents__current-step__text flow-list__contents__current-step__text--mobile flex-row">
          <span>
            {flow.currentStepsHeader}
            {renderWebhookIcon()}
          </span>
        </div>
        <div className="flow-list__contents__current-step__flowed-to flow-list__contents__current-step__flowed-to--mobile flex-row">
          Flowed to {flow.waitingForName}{' '}
          {flowinglyMomentService.calculateRelativeTimeOrFullDate(
            flow.waitingForDate,
            'LL'
          )}
        </div>
        <div className="flow-list__contents__due__type--mobile flex flex-row">
          <div className="flow-list__contents__due--mobile">
            {flow.isFinalised && (
              <div className="flow-list__contents__due-date__text flow-list__contents__due-date__text--mobile">
                {flow.finalisedReason} {flow.momentFinalised}
                <div className="flow-list__contents__subject__started-by">
                  {flow.momentFinalisedInLocalFormat}
                </div>
              </div>
            )}

            {!flow.isFinalised && flow.isOverdue && (
              <div className="flow-list__contents__due-date__text flow-list__contents__due-date__text--mobile color-warning flex-row">
                <i
                  className="fa-light fa-calendar-xmark"
                  aria-hidden="true"
                ></i>{' '}
                {flow.momentDue}
              </div>
            )}

            {!flow.isFinalised && !flow.isOverdue && flow.momentDue && (
              <div className="flow-list__contents__due-date__text flow-list__contents__due-date__text--mobile">
                {flow.momentDue !== 'No Due Date' && (
                  <i
                    className="fa-light fa-calendar-check"
                    aria-hidden="true"
                  ></i>
                )}
                {flow.momentDue}
              </div>
            )}

            {flow.momentDue !== 'No Due Date' && (
              <div className="flow-list__contents__due-date__due-by">
                {flow.isFinalised
                  ? flow.finalisedInLocalFormat
                  : flow.dueDateInLocalFormat}
              </div>
            )}
          </div>

          {showConfidentialFlowOption && (
            <div>
              <div className="flow-list__contents__workflow-type__header--mobile">
                Flow Type
              </div>

              <div className="flow-list__contents__workflow-type__text">
                <div
                  className={
                    flow.isConfidential
                      ? 'flow-list__contents__workflow-type__confidential'
                      : 'flow-list__contents__workflow-type__normal'
                  }
                >
                  {flow.isConfidential ? 'Confidential Workflow' : 'Workflow'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlowSummary;

export interface IFlowSummary extends IFlowForDisplay {
  finalisedInLocalFormat?: string;
  integrationState?: IntegrationState;
}
